import React, { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SystemSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/settings?category=aging`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(settings.map(s => 
      s.key === key ? { ...s, value } : s
    ));
  };

  const handleSave = async (setting) => {
    setSaving(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/settings/${setting.key}`,
        { value: setting.value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ type: 'success', text: 'Settings saved! Algorithm restarted with new configuration.' });
      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      console.error('Error saving setting:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-900">Aging Algorithm Configuration</h2>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <AlertCircle className="w-5 h-5" />
          <p>{message.text}</p>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">How Aging Works</h3>
        <p className="text-sm text-blue-800">
          The aging algorithm automatically increases task priority over time to prevent low-priority tasks from being forgotten.
          Tasks gain priority based on how long they've been waiting.
        </p>
      </div>

      <div className="space-y-4">
        {settings.map((setting) => (
          <div key={setting.id} className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">
                  {setting.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h3>
                {setting.description && (
                  <p className="text-sm text-gray-600">{setting.description}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4">
              {setting.key === 'aging_enabled' ? (
                <select
                  value={setting.value}
                  onChange={(e) => handleChange(setting.key, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              ) : (
                <input
                  type="number"
                  value={setting.value}
                  onChange={(e) => handleChange(setting.key, e.target.value)}
                  step={setting.key === 'aging_increment' ? '0.1' : '1'}
                  min={setting.key === 'aging_increment' ? '0.1' : '1'}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              )}
              
              <button
                onClick={() => handleSave(setting)}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>

            {setting.key === 'aging_interval_minutes' && (
              <p className="text-xs text-gray-500 mt-2">
                Current: Algorithm runs every {setting.value} minutes
              </p>
            )}
            {setting.key === 'aging_increment' && (
              <p className="text-xs text-gray-500 mt-2">
                Current: Tasks gain {setting.value} priority points every hour
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes</h3>
        <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
          <li>Changes take effect immediately and restart the aging algorithm</li>
          <li>Lower increment values = slower aging (tasks stay at original priority longer)</li>
          <li>Higher increment values = faster aging (tasks escalate more quickly)</li>
          <li>Recommended increment: 0.3 - 0.7 for balanced aging</li>
        </ul>
      </div>
    </div>
  );
};

export default SystemSettings;
